/**
 * 领料单 — the join application.
 *
 * A literal club requisition form, because that is what joining actually is. It
 * posts to `/api/public/applications` and surfaces the backend's field-level
 * validation verbatim, so a correction lands on the field that caused it instead
 * of on a generic banner.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError, type FieldErrors, type PublicContent, type PublicDepartment } from "../../lib/api.ts";
import { DesignatorTag, Field, Plate, SectionTitle, Select, TextArea, TextInput } from "../../components/kit.tsx";

const CLASS_NAME_HINT = '形如 "24通信01"：两位年份 + 中文专业 + 两位班号';

interface Submission {
  id: string;
  status: string;
}

export function Join() {
  const [departments, setDepartments] = useState<PublicDepartment[]>([]);
  // What already exists, so the applicant can see the club before applying rather
  // than after. Sourced from endpoints the public site already reads: nothing here
  // needs a new question for the backend to answer.
  const [existing, setExisting] = useState<{ projects: number; achievements: number } | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<Submission | null>(null);

  useEffect(() => {
    void api
      .get<PublicDepartment[]>("/api/public/departments")
      .then((list) => setDepartments(list.filter((department) => department.isOpen)))
      .catch(() => setDepartments([]));
    void api
      .get<PublicContent>("/api/public/content")
      .then((content) => setExisting({ projects: content.projects.length, achievements: content.achievements.length }))
      .catch(() => setExisting(null));
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    // Honeypot. A real applicant never sees this field; a bot fills it. It is
    // sent empty on purpose and never rendered.
    const payload = Object.fromEntries(form.entries());
    setSubmitting(true);
    setErrors({});
    setFormError(null);

    try {
      const result = await api.post<Submission>("/api/public/applications", payload, { skipCsrf: true });
      setDone(result);
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.code === "RATE_LIMITED") {
          setRateLimitedUntil(Date.now() + 30 * 60 * 1000);
        } else if (Object.keys(error.fields).length > 0) {
          setErrors(error.fields);
        } else {
          setFormError(error.message);
        }
      } else {
        setFormError("提交失败，请稍后重试。");
      }
    } finally {
      setSubmitting(false);
    }
  }

  // The backend refuses a student id that is already registered and says so on that
  // field. Detected from the field error rather than from a separate probe: asking
  // "is this id taken?" before submitting would turn the public form into a roster
  // lookup, which is the one thing this endpoint must not be.
  const studentIdTaken = Boolean(errors.studentId?.[0]?.includes("已在册"));

  if (done) {
    return (
      <main id="main" className="mx-auto flex min-h-[70vh] max-w-2xl flex-col justify-center px-6 py-24">
        <SectionTitle>RECEIPT / 已受理</SectionTitle>
        <h1 className="silk mt-6 text-[clamp(2rem,5vw,3.25rem)]">领料单已受理</h1>
        <Plate className="mt-10 p-8">
          <dl className="grid grid-cols-2 gap-6">
            <div>
              <dt className="silk-label text-[10px] text-silk-dim">单号</dt>
              <dd className="data mt-1.5 text-[14px] text-copper">{done.id}</dd>
            </div>
            <div>
              <dt className="silk-label text-[10px] text-silk-dim">状态</dt>
              <dd className="mt-1.5 text-[14px] text-silk">待部门审阅</dd>
            </div>
          </dl>
          <p className="mt-8 text-[13px] leading-relaxed text-silk-dim">
            负责人会审阅你的意向部门。审阅通过后，管理员会在成员中心为你创建账号并发一次性激活码。
            如果方向需要调整，负责人会直接联系你。
          </p>
        </Plate>
        <Link to="/" className="silk-label mt-8 inline-flex items-center gap-2 text-[11px] text-silk-dim hover:text-silk">
          <span aria-hidden>←</span> 返回首页
        </Link>
      </main>
    );
  }

  return (
    <main id="main" className="mx-auto max-w-3xl px-6 py-16 md:py-24">
      <Link to="/" className="silk-label inline-flex items-center gap-2 text-[11px] text-silk-dim hover:text-silk">
        <span aria-hidden>←</span> 返回首页
      </Link>

      <div className="mt-10 flex flex-col gap-4">
        <SectionTitle>REQUISITION / 领料单</SectionTitle>
        <h1 className="silk text-[clamp(2rem,5vw,3.25rem)]">领用你的第一个元件</h1>
        <p className="max-w-[54ch] text-[14px] leading-relaxed text-silk-dim">
          填完这张单子就算提交了加入申请。我们会看你的意向方向，不看绩点。
          带 * 的是必填项。
        </p>
      </div>

      {rateLimitedUntil ? (
        <div className="mt-8 border border-state-pending bg-ground-sunken px-5 py-4">
          <p className="text-[13px] text-silk">提交次数已达上限。</p>
          <p className="mt-1 text-[12px] text-silk-dim">
            同一份申请 30 分钟内最多提交三次，其中校验失败不计入次数。可以稍后再来，或直接发邮件联系我们。
          </p>
        </div>
      ) : null}

      {formError ? (
        <div role="alert" className="mt-8 border border-state-danger bg-ground-sunken px-5 py-4">
          <p className="text-[13px] text-silk">{formError}</p>
        </div>
      ) : null}

      {studentIdTaken ? (
        // The backend already tells a returning member to log in. Giving them a link
        // is the difference between an instruction and a way out: the form's only
        // other exit is the back link to the home page, which is not what somebody
        // who is already a member needs.
        <div className="mt-6 border border-line bg-ground-sunken px-5 py-4">
          <p className="text-[13px] text-silk">这个学号已经在册了。</p>
          <p className="mt-1 text-[12px] leading-relaxed text-silk-dim">
            如果这是你的学号，不用再申请：直接登录就行。密码忘了就找管理员重置。
          </p>
          <Link
            to="/sign-in"
            className="silk-label mt-3 inline-flex items-center gap-2 text-[11px] text-silk underline"
          >
            去登录
          </Link>
        </div>
      ) : null}

      <ExistingSnapshot departments={departments} existing={existing} />

      <form onSubmit={handleSubmit} noValidate className="mt-10 flex flex-col gap-6">
        <Plate className="p-6 md:p-8">
          <div className="mb-6 flex items-center justify-between">
            <DesignatorTag code="J1" label="申请人" />
            <span className="silk-label text-[10px] text-silk-dim">第一部分</span>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Field label="姓名" required error={errors.name?.[0]} htmlFor="name">
              <TextInput id="name" name="name" autoComplete="name" placeholder="你的姓名" />
            </Field>

            <Field label="学号" required error={errors.studentId?.[0]} htmlFor="studentId">
              <TextInput id="studentId" name="studentId" inputMode="numeric" placeholder="20240001" />
            </Field>

            <Field label="班级" required hint={CLASS_NAME_HINT} error={errors.className?.[0]} htmlFor="className">
              <TextInput id="className" name="className" placeholder="24通信01" />
            </Field>

            <Field label="意向部门" required error={errors.departmentId?.[0]} htmlFor="departmentId">
              <Select id="departmentId" name="departmentId" defaultValue="">
                <option value="" disabled>
                  选择一个正在招募的部门
                </option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="联系方式" required hint="微信号、手机号或邮箱，任选其一" error={errors.contact?.[0]} htmlFor="contact">
              <TextInput id="contact" name="contact" placeholder="便于负责人联系你" />
            </Field>

            <Field label="邮箱（选填）" error={errors.email?.[0]} htmlFor="email">
              <TextInput id="email" name="email" type="email" placeholder="用于接收后续通知" />
            </Field>
          </div>

          <div className="mt-6">
            <Field
              label="想做什么"
              required
              hint="至少 10 个字。说你想解决的真问题，比说你会什么技术更有用。"
              error={errors.motivation?.[0]}
              htmlFor="motivation"
            >
              <TextArea id="motivation" name="motivation" rows={5} />
            </Field>
          </div>

          <div className="mt-6">
            <Field label="作品或过程链接（选填）" htmlFor="portfolio">
              <TextInput id="portfolio" name="portfolio" placeholder="https://" />
            </Field>
          </div>

          {/* Honeypot: hidden from humans, irresistible to bots. */}
          <div aria-hidden className="absolute h-0 w-0 overflow-hidden opacity-0">
            <label>
              网址
              <input type="text" name="website" tabIndex={-1} autoComplete="off" />
            </label>
          </div>
        </Plate>

        <Plate className="p-6 md:p-8">
          <div className="mb-6 flex items-center justify-between">
            <DesignatorTag code="J2" label="知悉" />
            <span className="silk-label text-[10px] text-silk-dim">第二部分</span>
          </div>

          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              name="consent"
              value="accepted"
              className="mt-1 h-4 w-4 shrink-0 accent-[var(--signal)]"
            />
            <span className="text-[13px] leading-relaxed text-silk-dim">
              我已阅读招新说明，了解这是一个学生社团，活动时间与内容由成员共同安排。
            </span>
          </label>
          {errors.consent ? (
            <p className="mt-2 text-[12px] text-state-danger">{errors.consent[0]}</p>
          ) : null}

          <button
            type="submit"
            disabled={submitting || rateLimitedUntil !== null}
            className="silk-label mt-8 w-full border border-signal bg-signal px-6 py-3.5 text-[13px] text-silk-inverse transition-colors hover:bg-signal/90 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? "提交中" : "提交领料单"}
          </button>
        </Plate>
      </form>
    </main>
  );
}

/**
 * What already exists, before anybody applies.
 *
 * An applicant deciding whether to spend twenty minutes on a form is deciding it
 * against what the club already has, and the form asked for that decision with no
 * information at all. Everything here is read from endpoints the public site
 * already calls — the open departments for the form itself, the public content for
 * the counts.
 *
 * What is deliberately absent: inventory and fund balances. Those are internal
 * numbers about shared assets, they are not exposed by any public route, and an
 * applicant has no reason to be shown them before they are a member.
 */
function ExistingSnapshot({
  departments,
  existing,
}: {
  departments: PublicDepartment[];
  existing: { projects: number; achievements: number } | null;
}) {
  return (
    <section className="mt-10">
      <SectionTitle>EXISTING / 申请前先看看</SectionTitle>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Plate className="p-5">
          <p className="silk-label text-[10px] text-silk-dim">正在招人</p>
          <p className="silk mt-2 text-[2rem] leading-none">{departments.length}</p>
          <p className="mt-1 text-[11px] text-silk-dim">个部门开放申请</p>
        </Plate>
        <Plate className="p-5">
          <p className="silk-label text-[10px] text-silk-dim">公开项目</p>
          <p className="silk mt-2 text-[2rem] leading-none">{existing?.projects ?? "—"}</p>
          <p className="mt-1 text-[11px] text-silk-dim">首页能看到的那些</p>
        </Plate>
        <Plate className="p-5">
          <p className="silk-label text-[10px] text-silk-dim">已交付成果</p>
          <p className="silk mt-2 text-[2rem] leading-none">{existing?.achievements ?? "—"}</p>
          <p className="mt-1 text-[11px] text-silk-dim">已经交出去的</p>
        </Plate>
      </div>

      {departments.length > 0 ? (
        <ul className="mt-4 flex flex-col gap-2">
          {departments.map((department) => (
            <li key={department.id} className="border border-line px-4 py-3">
              <p className="text-[13px] text-silk">{department.name}</p>
              {department.description ? (
                <p className="mt-1 text-[12px] leading-relaxed text-silk-dim">{department.description}</p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <p className="mt-4 text-[11px] leading-relaxed text-silk-dim">
        库存和经费属于内部账目，加入之后才看得到；学号是否已在册由提交时判断，这里不做查询。
      </p>
    </section>
  );
}
